import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { AccountType, IAccount } from '../../interfaces/accounting.interface';
import { today } from './money';
import { useAccountingInstitution } from './useAccountingInstitution';

const SECTIONS: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY'];

/**
 * One-time entry of what the business already had on the day it starts using the books. Any
 * difference between assets and liabilities + equity is balanced to Opening balance equity.
 */
const OpeningBalances: React.FC = () => {
    const institutionId = useAccountingInstitution();
    const navigate = useNavigate();
    const { t } = useTranslation('accounting');
    const { showSuccess, showApiError } = useToast();
    const [accounts, setAccounts] = useState<IAccount[]>([]);
    const [asOf, setAsOf] = useState(today());
    const [amounts, setAmounts] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!institutionId) return;
        AccountingService.listAccounts(institutionId)
            .then(all => setAccounts(all.filter(a => a.active && a.systemKey !== 'OPENING_BALANCE_EQUITY'
                && ['ASSET', 'LIABILITY', 'EQUITY'].includes(a.type))))
            .catch(error => showApiError(error, t('errors.loadAccounts')));
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const lines = accounts
        .map(a => ({ accountId: a.id, amount: parseFloat(amounts[a.id] ?? '') }))
        .filter(l => Number.isFinite(l.amount) && l.amount > 0);

    const save = async () => {
        setSaving(true);
        try {
            await AccountingService.openingBalances(institutionId, { asOf, lines });
            showSuccess(t('openingBalances.posted'));
            navigate('/accounting');
        } catch (error) {
            showApiError(error, t('openingBalances.postFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>{t('openingBalances.title')}</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
                {t('openingBalances.intro')}
            </Alert>
            <Paper sx={{ p: 2 }}>
                <TextField id="ob-as-of" label={t('fields.asOf')} type="date" size="small" value={asOf}
                           onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
                {SECTIONS.map(type => {
                    const rows = accounts.filter(a => a.type === type);
                    if (rows.length === 0) return null;
                    return (
                        <Box key={type} sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t(`accountTypes.${type}`)}</Typography>
                            <Stack spacing={1.5}>
                                {rows.map(a => (
                                    <TextField key={a.id} id={`ob-${a.id}`} label={`${a.code} · ${a.name}`} type="number"
                                               size="small" value={amounts[a.id] ?? ''}
                                               onChange={e => setAmounts(prev => ({ ...prev, [a.id]: e.target.value }))}
                                               inputProps={{ min: 0, step: '0.01' }} />
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
                <Button variant="contained" disabled={lines.length === 0 || !asOf || saving} onClick={save}>
                    {t('openingBalances.post')}
                </Button>
            </Paper>
        </Box>
    );
};

export default OpeningBalances;
